<template>
  <div class="h-full flex flex-col">
    <span class="input-label">{{ $t('App.SearchRoomLabel') }}</span>
    <div class="mb-3">
      <div class="flex gap-2">
        <input
          v-model="search"
          class="input grow"
          type="search"
          :placeholder="$t('App.SearchRoomPlaceholder')"
          @input="update"
        >
        <button
          v-tippy="$t('App.AdvancedSearch')"
          class="btn btn-secondary btn-square"
          :class="{ '!text-microsoft-blue-default': showAdvanced }"
          @click="showAdvanced = !showAdvanced"
        >
          <div
            v-if="filterCount"
            class="btn-badge"
          >
            {{ filterCount }}
          </div>
          <FilterIcon class="w-5 h-5" />
        </button>
      </div>
      <CollapseTransition>
        <advanced-search
          v-show="showAdvanced"
          :available-equipment="availableEquipment"
          :buildings="buildings"
          :building="building"
          :locations="locations"
          :location="location"
          :capacities="capacities"
          :capacity="capacity"
          :equipment="equipment"
          :only-free="onlyFree"
          @update-capacity="updateCapacity"
          @update-equipment="updateEquipment"
          @update-building="updateBuilding"
          @update-location="updateLocation"
          @update-only-free="updateOnlyFree"
          @search="update"
        />
      </CollapseTransition>
    </div>
    <app-loading
      v-if="loading"
      :loading="$t('App.LoadingData')"
      class="flex flex-col justify-start mt-5 items-center grow"
    />
    <div
      v-else
      class="flex-grow flex flex-col"
    >
      <div class="flex items-center gap-4 text-neutral-gray-500">
        <div class="divider grow" />
        <div
          v-if="filteredBookables.length"
          class="text-sm"
        >
          {{ $tc('App.RoomsFound', filteredBookables.length, { count: filteredBookables.length }) }}
        </div>
      </div>
      <div
        v-if="filteredBookables.length > 0"
        class="flex flex-col gap-3 mt-3"
      >
        <Card
          v-for="bookable of filteredBookables"
          :key="bookable.id + 'card'"
          :subtitle="bookable.integration_email"
          :action="{
            label: $t('App.AddRoom'),
            icon: SquarePlus,
            onClick: () => outlook.addAttendee(bookable.integration_email)
          }"
          @open="$emit('select-bookable', bookable)"
        >
          <template #title>
            {{ bookable.name }}
            <FavouriteButton
              :id="bookable.id"
              type="bookable"
            />
          </template>
          <template #details>
            <BookableStatus
              :booked="!!bookable.bookings.length"
              :booked-by-current-event="bookable.bookings.some(booking => booking.event_id === flexopusEventId)"
            />
            {{ bookable.location?.building?.name }},
            {{ bookable.location?.name }}
          </template>
        </Card>
      </div>
      <div v-else-if="filteredBookables.length === 0">
        <h5 class="mt-6 font-medium text-neutral-gray-700 text-center">
          {{ $t('App.NoResults') }}
        </h5>
      </div>
    </div>
  </div>
</template>

<script>
import AdvancedSearch from '@/components/AdvancedSearch.vue'
import AppLoading from '@/components/AppLoading.vue'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import { Filter as FilterIcon, SquarePlus } from 'lucide-vue'
import Card from './Card.vue'
import outlook from '@/outlook'
import BookableStatus from './BookableStatus.vue'
import FavouriteButton from './FavouriteButton.vue'
import CollapseTransition from './Transitions/CollapseTransition.vue'

export default {
  components: {
    AdvancedSearch,
    AppLoading,
    Card,
    BookableStatus,
    FilterIcon,
    FavouriteButton,
    CollapseTransition,
  },
  props: {
    attendees: {
      required: true,
      type: Array,
    },
    loading: {
      required: true,
      type: Boolean,
    },
    availableEquipment: {
      required: true,
      type: Array,
    },
    bookables: {
      required: true,
      type: Array,
    },
    buildings: {
      required: true,
      type: Array,
    },
    locations: {
      required: true,
      type: Array,
    },
    params: {
      required: true,
      type: Object,
    },
    flexopusEventId: {
      type: Number,
      default: null,
    },
  },
  data () {
    return {
      capacity: null,
      search: '',
      equipment: [],
      building: null,
      location: null,
      onlyFree: true,
      showAdvanced: false,
      outlook,
      SquarePlus,
    }
  },
  computed: {
    filterCount () {
      return Object.entries(this.params).filter(([key, value]) => {
        switch (key) {
          case 'search':
            return !!value
          case 'min_capacity':
            return value || this.params.max_capacity !== undefined
          case 'max_capacity':
            return false
          default:
            return value !== undefined
        }
      }).length
    },
    capacities () {
      return [
        { name: this.$t('App.AllRooms'), value: [0] },
        ...['1-4', '5-9', '10-14', '15-19', '20-29', '30-49', '50-99', '100']
          .map((c, index, arr) => ({
            name: index === arr.length - 1 ? `${c}+` : c,
            value: c.split('-').map(n => +n),
          })),
      ]
    },
    filteredBookables () {
      return this.bookables.filter(
        bookable => !bookable.bookings.length ||
          !this.onlyFree ||
          bookable.bookings.some(booking => booking.event_id === this.flexopusEventId),
      )
    },
  },
  mounted () {
    this.initFromParams()
  },
  methods: {
    initFromParams () {
      this.search = this.params.search ?? ''

      if (this.params.equipment && this.params.equipment.length > 0) {
        this.equipment = this.availableEquipment
          .filter(e => this.params.equipment.includes(e.id)) ?? []
      }

      this.capacity = this.capacities.find(c => isEqual(
        c.value,
        [this.params.min_capacity, this.params.max_capacity].filter(v => v !== undefined),
      )) ?? null

      this.location = this.locations.find(l => l.id === this.params.location) ?? null

      this.building = this.buildings.find(b => b.id === this.params.building) ?? null

      this.showAdvanced = this.equipment.length > 0 || this.capacity !== null || this.location !== null || this.building !== null || !this.onlyFree
    },
    updateBuilding (building) {
      this.building = building
      if (building && this.location && building.id !== this.location.building_id) {
        this.location = null
      }
    },
    updateCapacity (capacity) {
      this.capacity = capacity
    },
    updateLocation (location) {
      this.location = location
    },
    updateEquipment (equipment) {
      this.equipment = [...equipment]
    },
    updateOnlyFree (value) {
      this.onlyFree = value
    },
    update: debounce(function () {
      const params = {
        search: this.search,
        min_capacity: this.capacity?.value && typeof this.capacity.value[0] === 'number' ? this.capacity.value[0] : undefined,
        max_capacity: this.capacity?.value && this.capacity.value[1] ? this.capacity.value[1] : undefined,
        building: this.building ? this.building.id : undefined,
        location: this.location ? this.location.id : undefined,
        equipment: this.equipment && this.equipment.length > 0 ? this.equipment.map(e => e.id) : undefined,
      }

      if (isEqual(this.params, params)) {
        return
      }

      this.$emit('search', params)
    }, 300),
  },
}
</script>

<style lang="scss" scoped>
.arrow-icon {
  height: 0.4375rem;
}

.loading {
  width: 4rem;
}

.inverted {
  transform: scaleY(-1);
}
</style>
